/**
 * The places there are, going to one, and making another.
 *
 * A dungeon is played as a copy nobody can save, so it has to be built in the
 * one everybody shares. This is the way there and the way back, and the way to
 * start somewhere that does not exist yet: a continent, an island off it, a
 * cellar under a shop.
 *
 * The list comes from the server rather than from anything written here, so a
 * region file somebody adds turns up without a release.
 */

import { useEffect, useState } from 'react';
import { placesClashing, type Place } from './places-list';
import { Explain } from '../Explain';

export function Places({
  here,
  places,
  onGo,
  onMake,
  onArrange,
  onRefresh,
}: {
  /** The region being stood in, so the one somebody is already in reads as such. */
  here: string;
  places: readonly Place[];
  onGo: (region: string) => void;
  onMake: (made: {
    region: string;
    name: string;
    width: number;
    height: number;
    underground: boolean;
  }) => void;
  /** Opens the map of everywhere, on a place, where it can be dragged about. */
  onArrange: (region?: string) => void;
  onRefresh: () => void;
}): JSX.Element {
  const [making, setMaking] = useState(false);
  const [looking, setLooking] = useState('');
  const [name, setName] = useState('');
  const [size, setSize] = useState(64);
  const [underground, setUnderground] = useState(false);

  // Asked for once, when the panel is first drawn, and again whenever somebody
  // makes a place.
  useEffect(() => {
    onRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const id = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48);

  const clashing = placesClashing(places);

  // Where somebody is now, first, and then everything else by name. A world
  // with dozens of places in it is a list, and a list is read from the top.
  const wanted = looking.trim().toLowerCase();
  const showing = [...places]
    .filter((place) => wanted.length === 0 || place.name.toLowerCase().includes(wanted))
    .sort((one, other) => {
      if (one.id === here) return -1;
      if (other.id === here) return 1;
      if (one.layer !== other.layer) return one.layer - other.layer;
      return one.name.localeCompare(other.name);
    });

  return (
    <div className="editor-section">
      <h4>Places</h4>

      {/* Where a place sits in the world is a thing to see rather than a pair
          of numbers to type. The map of everywhere is where it is arranged,
          by dragging it. */}
      <button type="button" className="wide primary" onClick={() => onArrange()}>
        Arrange them on the map
      </button>

      {places.length > 6 ? (
        <input
          className="editor-search"
          type="text"
          value={looking}
          placeholder="Find a place"
          onChange={(event) => setLooking(event.target.value)}
        />
      ) : null}

      {clashing.length > 0 ? (
        <p className="hint warn">
          {clashing.map((place) => place.name).join(' and ')} cover the same ground. Two places on
          one tile is whichever loaded last winning, quietly.
        </p>
      ) : null}

      {showing.map((place) => (
        <div key={place.id} className="editor-place">
          <button
            type="button"
            className={place.id === here ? 'wide on' : 'wide'}
            title={place.id === here ? 'The world is standing on this one' : `Go to ${place.name}`}
            onClick={() => onGo(place.id)}
          >
            {place.name}
            {place.id === here ? <span className="editor-count-quiet"> you are here</span> : null}
          </button>

          <div className="editor-brushes">
            <span className="editor-count-quiet">
              {place.width} by {place.height}
              {place.layer === 0
                ? ''
                : place.layer < 0
                  ? ', underground'
                  : `, layer ${place.layer}`}
            </span>
            <button
              type="button"
              className="editor-tab"
              title={
                place.id === here
                  ? 'The world is standing on this one, so it stays where it is'
                  : 'Show it on the map of everywhere, where it can be dragged into place'
              }
              disabled={place.id === here}
              onClick={() => onArrange(place.id)}
            >
              Move It
            </button>
          </div>
        </div>
      ))}

      {places.length > 0 && showing.length === 0 ? (
        <p className="hint">Nothing here by that name.</p>
      ) : null}

      {places.length === 0 ? <p className="hint">Asking the server what there is.</p> : null}

      {making ? (
        <>
          <label className="editor-field">
            <span>Name</span>
            <input
              type="text"
              value={name}
              placeholder="Sunken chapel"
              onChange={(event) => setName(event.target.value.slice(0, 48))}
            />
          </label>

          <label className="editor-field">
            <span>Size</span>
            <input
              type="number"
              min={16}
              max={256}
              step={16}
              value={size}
              onChange={(event) => setSize(Number(event.target.value))}
            />
          </label>

          <div className="editor-brushes">
            <button
              type="button"
              className={underground ? 'editor-tab' : 'editor-tab on'}
              onClick={() => setUnderground(false)}
            >
              Open air
            </button>
            <button
              type="button"
              className={underground ? 'editor-tab on' : 'editor-tab'}
              onClick={() => setUnderground(true)}
            >
              Underground
            </button>
          </div>

          <button
            type="button"
            className="wide"
            disabled={id.length < 3}
            onClick={() => {
              onMake({ region: id, name: name.trim(), width: size, height: size, underground });
              setName('');
              setMaking(false);
              // The server writes the file, so the list is asked for again a
              // moment later rather than guessed at here.
              window.setTimeout(onRefresh, 900);
            }}
          >
            Make it
          </button>
        </>
      ) : (
        <button type="button" className="wide" onClick={() => setMaking(true)}>
          Somewhere new
        </button>
      )}

      <p className="hint">
        These are the shared ones, which are the ones that save.
        <Explain>
          A door in the world makes a copy for whoever goes through it, and a copy is never written
          out. Underground starts as solid rock with a room in the middle of it, to be carved out
          from there.
        </Explain>
      </p>
    </div>
  );
}
